// AnimePage - Профессиональная версия с полной интеграцией бэкенд API
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './AnimePage.module.scss'
import WelcomeHeader from '../../WelcomePage/components/WelcomeHeader/WelcomeHeader'
import { 
  getAnimes, 
  getAnimeGenres, 
  likeAnime
} from '../../../service/AnimeService'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import ImageWithFallback from '../../../components/ImageWithFallback/ImageWithFallback'
import { AnimeIcon, StarIcon, SearchIcon } from '../../../components/CustomIcons'
import { addItemToPanel, getMyPanel } from '../../../service/PersonalPanelService'

const AnimePage = ({ embedded = false, goBack }) => {
  const navigate = useNavigate()
  const [animes, setAnimes] = useState([])
  const [genres, setGenres] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [filters, setFilters] = useState({
    search: '',
    genre: '',
    type: '',
    year: ''
  })
  const [sortBy, setSortBy] = useState('-rating_avg')
  const [likedAnimes, setLikedAnimes] = useState(new Set())
  const [panelAnimeIds, setPanelAnimeIds] = useState(new Set())

  useEffect(() => {
    loadData()
    loadPanelAnimeIds()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy])

  const loadPanelAnimeIds = async () => {
    if (!localStorage.getItem('token')) return
    try {
      const response = await getMyPanel()
      const items = response.data?.items || []
      const ids = items
        .filter((item) => item.item_type === 'anime')
        .map((item) => String(item.item_id))
      setPanelAnimeIds(new Set(ids))
    } catch (error) {
      console.error('Error loading anime panel ids:', error)
    }
  }

  useEffect(() => {
    // Перезагрузка при изменении фильтров с возвратом на первую страницу
    if (currentPage !== 1) {
      setCurrentPage(1)
    } else {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.genre, filters.type, filters.year])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Формируем параметры, исключая пустые значения
      const params = {
        page: currentPage,
        ordering: sortBy
      }
      
      // Добавляем только непустые фильтры
      if (filters.search && filters.search.trim()) {
        params.search = filters.search.trim()
      }
      if (filters.genre) {
        params.category = filters.genre  // API ожидает 'category', не 'genre'
      }
      if (filters.type) {
        params.type = filters.type
      }
      if (filters.year) {
        params.year = filters.year
      }

      const [animesResponse, genresResponse] = await Promise.all([
        getAnimes(params),
        getAnimeGenres().catch(() => ({ data: { results: [] } }))
      ])
      
      // Обработка ответа от API
      const animesData = animesResponse?.results || animesResponse?.data?.results || animesResponse?.data || []
      const pages = animesResponse?.total_pages || animesResponse?.data?.total_pages || Math.ceil((animesResponse?.count || animesResponse?.data?.count || 0) / 20) || 1
      const count = animesResponse?.count || animesResponse?.data?.count || animesData.length
      
      setAnimes(animesData)
      setTotalPages(pages)
      setTotalCount(count)
      setGenres(genresResponse?.data?.results || genresResponse?.data || [])
      
      // Извлекаем лайкнутые аниме
      const liked = new Set(animesData.filter(anime => anime.is_liked).map(anime => anime.id))
      setLikedAnimes(liked)
      
    } catch (err) {
      setError(`Ошибка загрузки аниме: ${err.message}`)
      console.error('Error loading anime data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRetry = () => {
    loadData()
  }

  const handleAnimeClick = (anime) => {
    navigate(`/services/anime/watch/${anime.slug || anime.id}`, {
      state: {
        anime
      }
    })
  }

  const handleLikeAnime = async (anime, e) => {
    e.stopPropagation()
    try {
      await likeAnime(anime.slug || anime.id)
      
      setLikedAnimes(prev => {
        const newLiked = new Set(prev)
        if (newLiked.has(anime.id)) {
          newLiked.delete(anime.id)
        } else {
          newLiked.add(anime.id)
        }
        return newLiked
      })

      // Обновляем счётчик лайков в списке
      setAnimes(prevAnimes => 
        prevAnimes.map(a => 
          a.id === anime.id 
            ? { ...a, likes_count: likedAnimes.has(anime.id) ? (a.likes_count || 0) - 1 : (a.likes_count || 0) + 1 }
            : a
        )
      )
    } catch (err) {
      console.error('Error toggling like:', err)
    }
  }

  const handleWatchAnime = (anime, e) => {
    e.stopPropagation()

    navigate(`/services/anime/watch/${anime.slug || anime.id}`, {
      state: {
        anime
      }
    })
  }

  const handleAddAnimeToPanel = async (anime, e) => {
    e.stopPropagation()
    if (!localStorage.getItem('token')) {
      window.dispatchEvent(new CustomEvent('openAuth', { detail: true }))
      return
    }

    try {
      const panelId = String(anime.slug || anime.id)
      await addItemToPanel('anime', panelId, {
        title: anime.name || anime.title,
        poster: anime.poster,
        description: anime.description || '',
        rating: anime.rating_avg || null,
        episodes_total: anime.episodes_total || anime.episodes_count || 0
      })
      setPanelAnimeIds((prev) => new Set(prev).add(panelId))
    } catch (error) {
      if (error?.response?.status === 400) {
        setPanelAnimeIds((prev) => new Set(prev).add(String(anime.slug || anime.id)))
      } else {
        console.error('Error adding anime to panel:', error)
      }
    }
  }

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }))
    // Автоматически переходим на первую страницу при изменении фильтра
    if (currentPage !== 1) {
      setCurrentPage(1)
    }
  }

  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      // Автоматически переходим на первую страницу при поиске
      if (currentPage !== 1) {
        setCurrentPage(1)
      } else {
        loadData()
      }
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const getAnimeTypeLabel = (type) => {
    const types = {
      'tv': 'ТВ',
      'movie': 'Фильм',
      'ova': 'OVA',
      'ona': 'ONA',
      'special': 'Спешл'
    }
    return types[type] || type
  }

  const formatYear = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).getFullYear()
    } catch {
      return 'N/A'
    }
  }

  if (loading && animes.length === 0) {
    return (
      <div className={styles.page}>
        {!embedded && <WelcomeHeader />}
        <div className={styles.container}>
          <div className={styles.loadingContainer}>
            <Loader size="large" />
            <p>Загрузка аниме каталога...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {!embedded && <WelcomeHeader />}
      
      <div className={styles.container}>
        {/* <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>
              <AnimeIcon size={32} style={{ verticalAlign: 'middle', marginRight: '10px' }} /> Аниме
            </h1>
            <p className={styles.subtitle}>Каталог аниме и манги</p>
          </div>
        </div> */}

        {/* Поиск и фильтры */}
        <div className={styles.controls}>
          <div className={styles.topControls}>
            <div className={styles.searchContainer}>
              <input
                type="text"
                placeholder="Поиск аниме по названию..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onKeyPress={handleSearch}
                className={styles.searchInput}
              />
              <button onClick={handleSearch} className={styles.searchButton}>
                <SearchIcon size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Найти
              </button>
            </div>

            <div className={styles.statsInfo}>
              Найдено: <strong>{totalCount}</strong> {totalCount === 1 ? 'аниме' : 'аниме'}
            </div>
          </div>

          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label>Жанр:</label>
              <select
                value={filters.genre}
                onChange={(e) => handleFilterChange('genre', e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">Все жанры</option>
                {genres.map(genre => (
                  <option key={genre.id} value={genre.id}>
                    {genre.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Тип:</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">Все типы</option>
                <option value="tv">ТВ сериал</option>
                <option value="movie">Фильм</option>
                <option value="ova">OVA</option>
                <option value="ona">ONA</option>
                <option value="special">Спецвыпуск</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Год:</label>
              <select
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">Все годы</option>
                {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Сортировка:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="-rating_avg">По рейтингу</option>
                <option value="-created_at">🆕 Недавно добавленные</option>
                <option value="-release_date">По дате выхода</option>
                <option value="name">По названию (А-Я)</option>
                <option value="-name">По названию (Я-А)</option>
                <option value="-episodes_total">📺 По эпизодам</option>
              </select>
            </div>

            <button 
              onClick={() => {
                setFilters({ search: '', genre: '', type: '', year: '' })
                setSortBy('-rating_avg')
              }}
              className={styles.resetButton}
            >
              🔄 Сбросить
            </button>
          </div>
        </div>

        {error && (
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}><AnimeIcon size={64} /></div>
            <div className={styles.errorContent}>
              <h3 className={styles.errorTitle}>Ошибка загрузки каталога</h3>
              <p className={styles.errorMessage}>{error}</p>
              <button 
                onClick={handleRetry}
                className={styles.retryButton}
                disabled={loading}
              >
                {loading ? 'Загрузка...' : 'Повторить попытку'}
              </button>
            </div>
          </div>
        )}

        {/* Сетка аниме */}
        <div className={styles.animeGrid}>
          {animes.map((anime) => (
            <div 
              key={anime.id} 
              className={styles.animeCard}
              onClick={() => handleAnimeClick(anime)}
            >
              <div className={styles.animePoster}>
                <ImageWithFallback
                  src={anime.poster}
                  alt={anime.name || anime.title}
                  fallbackIcon="🎌"
                  fallbackText="Нет постера"
                  className={styles.posterImage}
                />
                <div className={styles.animeOverlay}>
                  <button 
                    className={styles.playButton}
                    onClick={(e) => handleWatchAnime(anime, e)}
                  >
                    ▶️ Смотреть
                  </button>
                  <button 
                    className={`${styles.likeButton} ${likedAnimes.has(anime.id) ? styles.liked : ''}`}
                    onClick={(e) => handleLikeAnime(anime, e)}
                  >
                    {likedAnimes.has(anime.id) ? '❤️' : '🤍'}
                  </button>
                </div>
                <div className={styles.typeBadge}>
                  {getAnimeTypeLabel(anime.type)}
                </div>
              </div>
              
              <div className={styles.animeInfo}>
                <h3 className={styles.animeTitle}>
                  {anime.name || anime.title}
                </h3>
                
                {anime.original_name && (
                  <p className={styles.originalName}>{anime.original_name}</p>
                )}
                
                <div className={styles.animeDetails}>
                  <span className={styles.animeEpisodes}>
                    📺 {anime.episodes_total || anime.episodes_count || 0} эп.
                  </span>
                  <span className={styles.animeYear}>
                    📅 {formatYear(anime.release_date)}
                  </span>
                </div>
                
                <div className={styles.animeStats}>
                  <div className={styles.animeRating}>
                    <StarIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{anime.rating_avg ? Number(anime.rating_avg).toFixed(1) : 'N/A'}
                  </div>
                  {anime.likes_count !== undefined && anime.likes_count > 0 && (
                    <div className={styles.animeLikes}>
                      ❤️ {anime.likes_count}
                    </div>
                  )}
                  {anime.reviews_count !== undefined && anime.reviews_count > 0 && (
                    <div className={styles.animeReviews}>
                      💬 {anime.reviews_count}
                    </div>
                  )}
                </div>

                {anime.studio && (
                  <div className={styles.animeStudio}>
                    🎬 {anime.studio}
                  </div>
                )}

                {anime.categories && anime.categories.length > 0 && (
                  <div className={styles.animeGenres}>
                    {anime.categories.slice(0, 3).map((category, idx) => (
                      <span key={category.id || idx} className={styles.genreTag}>
                        {category.name || category}
                      </span>
                    ))}
                  </div>
                )}

                {anime.description && (
                  <p className={styles.animeDescription}>
                    {anime.description.length > 100 
                      ? `${anime.description.substring(0, 100)}...` 
                      : anime.description}
                  </p>
                )}

                <div className={styles.animeCardActions}>
                  <button
                    type="button"
                    className={`${styles.addToPanelButton} ${panelAnimeIds.has(String(anime.slug || anime.id)) ? styles.addToPanelButtonAdded : ''}`}
                    onClick={(e) => handleAddAnimeToPanel(anime, e)}
                  >
                    {panelAnimeIds.has(String(anime.slug || anime.id)) ? 'Уже в панели' : 'В панель'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {animes.length === 0 && !loading && !error && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><SearchIcon size={64} /></div>
            <h3>Аниме не найдено</h3>
            <p>Попробуйте изменить параметры поиска или фильтры</p>
          </div>
        )}

        {/* Пагинация */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

    </div>
  )
}

export default AnimePage